import { useEffect, useMemo, useState } from "react";
import { MainLayoutContainer } from "components/Layout";
import { useParams } from "react-router-dom";
import { recordEducationRecordRead } from "services/educationRecordAccessLog";
import { Tab, Tabs } from "@mui/material";
import { useGetFeedbacksByStudentIdQuery } from "graphql/__generated__/hooks";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { TABLE_ACTIONS } from "helpers/constants";
import { ROLES } from "types/user";
import { TabPanel } from "components/TabPanel";
import { FeedbackTable } from "./components";
import { shouldHideFeedback } from "utils/feedback";

export const OrganizationUserFeedbackList = () => {
  const [value, setValue] = useState(0);
  const { organizationId, userId } = useParams<{
    organizationId: string;
    userId: string;
  }>();

  const { data, loading } = useGetFeedbacksByStudentIdQuery({
    variables: { studentId: userId ?? "" },
    skip: !userId,
  });

  useEffect(() => {
    if (userId) {
      recordEducationRecordRead({
        resourceType: "student_feedback_list",
        resourceId: userId,
      });
    }
  }, [userId]);

  const feedbacks = useMemo<FeedbackType[]>(() => {
    return (data?.feedback as FeedbackType[]) ?? [];
  }, [data?.feedback]);

  const feedbackCounter = useMemo(() => {
    const draft = feedbacks.filter((f) => f.status === FeedbackStatus.DRAFT).length;
    const nonDraft = feedbacks.filter(
      (f) =>
        f.status !== FeedbackStatus.DRAFT && !shouldHideFeedback(f),
    ).length;
    return { draft, nonDraft };
  }, [feedbacks]);

  const learnerName =
    feedbacks.length > 0
      ? (feedbacks[0] as any).student?.name
      : undefined;

  const title = learnerName
    ? `All feedback for ${learnerName}`
    : "All feedback";

  const handleActionClick = async (_action: TABLE_ACTIONS, _id: string) => {
    // Read-only list; no row actions
  };

  const feedbackDetailBasePath = organizationId
    ? `/organization/${organizationId}/feedback`
    : undefined;

  return (
    <MainLayoutContainer title={title} loading={loading}>
      <Tabs
        value={value}
        onChange={(_, newValue) => setValue(newValue)}
        aria-label="Feedback status"
      >
        <Tab
          label={`Active (${feedbackCounter.nonDraft})`}
          id="org-user-feedback-tab-0"
          aria-controls="org-user-feedback-tabpanel-0"
        />
        <Tab
          label={`Draft (${feedbackCounter.draft})`}
          id="org-user-feedback-tab-1"
          aria-controls="org-user-feedback-tabpanel-1"
        />
      </Tabs>

      <TabPanel value={value} index={0}>
        <FeedbackTable
          feedbacks={feedbacks}
          handleActionClick={handleActionClick}
          userRole={ROLES.COACH}
          excludeDraft
          readOnly
          feedbackDetailBasePath={feedbackDetailBasePath}
        />
      </TabPanel>

      <TabPanel value={value} index={1}>
        <FeedbackTable
          feedbacks={feedbacks}
          handleActionClick={handleActionClick}
          userRole={ROLES.COACH}
          status={FeedbackStatus.DRAFT}
          readOnly
          feedbackDetailBasePath={feedbackDetailBasePath}
        />
      </TabPanel>
    </MainLayoutContainer>
  );
};
